import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";

/** One programme assumption: its value (null: the default applies), what is used now, and what it means. */
interface Assumption { key: string; value: string | null; used: string; source: string; note: string }

/**
 * Programme assumptions: wave capacity, hardware lead time, what counts as a critical asset and what it is called,
 * the migration time to review, vendor escalation intervals, sponsor and lead. The same values the confirmation
 * workbook sets; an empty field returns to the default. Used by the next report, without a new run.
 */
export function ProgrammeAssumptionsSettings({ projectId }: { projectId: string }) {
  const auth = useAuth();
  const canEdit = auth.hasRole("admin", "super_user", "analyst");
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["programme-assumptions", projectId], queryFn: () => api.get<Assumption[]>(`/projects/${projectId}/deliverables/programme-assumptions`) });
  const [draft, setDraft] = useState<Record<string, string>>({});
  useEffect(() => { if (q.data) setDraft(Object.fromEntries(q.data.map((a) => [a.key, a.value ?? ""]))); }, [q.data]);
  const save = useMutation({
    mutationFn: () => api.put(`/projects/${projectId}/deliverables/programme-assumptions`, { values: Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, v.trim() || null])) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["programme-assumptions", projectId] }),
  });
  const rows = q.data ?? [];
  const dirty = rows.some((a) => (a.value ?? "") !== (draft[a.key] ?? ""));
  return (
    <section className="mt-8 pt-6 border-t border-ink-100 space-y-4" data-testid="programme-assumptions">
      <div>
        <h3 className="text-sm font-medium text-ink-900">Programme assumptions</h3>
        <p className="text-xs text-ink-500 mt-1">Used by the migration programme and the report pack from the next download. Leave a field empty to use the default shown.</p>
      </div>
      {q.isError && <p className="text-xs text-red-700" role="alert">The programme assumptions could not be loaded.</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        {rows.map((a) => (
          <label key={a.key} className="text-xs space-y-1">
            <span className="block font-medium text-ink-800">{a.key}</span>
            <input className="w-full border border-ink-200 rounded-sm px-2 py-1 text-xs disabled:bg-ink-50" disabled={!canEdit}
              placeholder={a.value ? "" : a.used} value={draft[a.key] ?? ""} onChange={(e) => setDraft({ ...draft, [a.key]: e.target.value })} data-testid={`assumption-${a.key}`} />
            <span className="block text-ink-500">{a.note}</span>
          </label>
        ))}
      </div>
      {canEdit && (
        <div className="flex items-center gap-3">
          <button type="button" className="text-xs rounded-sm px-3 py-1.5 bg-accent text-white disabled:opacity-50" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save programme assumptions"}
          </button>
          {save.isError && <span className="text-xs text-red-700" role="alert">{save.error instanceof Error ? save.error.message : "Not saved"}</span>}
          {save.isSuccess && !dirty && <span className="text-xs text-ink-500">Saved.</span>}
        </div>
      )}
    </section>
  );
}
